import { createReducer, on } from '@ngrx/store';
import { SortDirection } from '@shared/enums';
import { ReportDownloadInProgress } from '@shared/models/dpr';
import { AppActions } from './actions';
import { fromEvent, map, merge, of, take } from 'rxjs';

export const AppFeatureName = 'app';

export interface AppState {
  sidebarPinned: boolean;
  isFullScreenMode: boolean;
  isAppOnline: boolean;
  loginPageDisplayed: boolean;
  redirectedToCore: boolean;
  isLoading: boolean;
  requestCount: number;
  isAppLockedInOffline: boolean;
  dprsSortingDirection: SortDirection;
  reportDownloads: ReportDownloadInProgress[];
}

export const initialState: AppState = {
  sidebarPinned: false,
  isFullScreenMode: false,
  isAppOnline: isAppOnline(),
  loginPageDisplayed: false,
  redirectedToCore: false,
  isLoading: false,
  requestCount: 0,
  isAppLockedInOffline: false,
  dprsSortingDirection: SortDirection.Descending,
  reportDownloads: [],
};

export const appReducer = createReducer(
  initialState,
  on(AppActions.toggleSidebar, (state) => ({
    ...state,
    sidebarPinned: !state.sidebarPinned,
  })),
  on(AppActions.toggleFullScreenMode, (state) => ({
    ...state,
    isFullScreenMode: !state.isFullScreenMode,
  })),
  on(AppActions.setAppOnlineStatus, (state, { appOnline }) => ({
    ...state,
    isAppOnline: appOnline,
  })),
  on(AppActions.setLoginPageDisplayed, (state, { loginPageDisplayed }) => ({
    ...state,
    loginPageDisplayed,
  })),
  on(AppActions.setRedirectedToCore, (state, { redirectedToCore }) => ({
    ...state,
    redirectedToCore,
  })),
  on(AppActions.showLoader, (state) => ({
    ...state,
    isLoading: true,
    requestCount: state.requestCount + 1,
  })),
  on(AppActions.hideLoader, (state) => ({
    ...state,
    isLoading: state.requestCount <= 1 ? false : state.isLoading,
    requestCount: state.requestCount <= 1 ? 0 : state.requestCount - 1,
  })),
  on(AppActions.setAppLockedInOffline, (state, { appLockedInOffline }) => ({
    ...state,
    isAppLockedInOffline: appLockedInOffline,
  })),
  on(AppActions.setDprsSortingDirection, (state, { sortingDirection }) => ({
    ...state,
    dprsSortingDirection: sortingDirection,
  })),
  on(AppActions.setReportDownload, (state, { reportDownload }) => ({
    ...state,
    reportDownloads: [...state.reportDownloads, reportDownload],
  })),
  on(AppActions.clearReportDownload, (state, { dprDetailId }) => ({
    ...state,
    reportDownloads: state.reportDownloads.filter(
      (x) => x.dprDetailId !== dprDetailId
    ),
  })),
  on(AppActions.clearAppState, (_state) => ({
    ...initialState,
    isAppOnline: isAppOnline(),
  }))
);

function isAppOnline(): boolean {
  let networkStatus;
  merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
    .pipe(
      take(1),
      map(() => navigator.onLine)
    )
    .subscribe((status) => {
      networkStatus = status;
    });

  return networkStatus;
}
